
import { useState, useEffect } from 'react';
import Pagination from "../components/Pagination.jsx";
import { motion } from 'framer-motion';
import {
  Trash2,
  Edit,
  Plus,
  Mail,
  Phone,
  Star,
  Clock,
  DollarSign,
  AlertCircle,
  AlertTriangle,
  Check,
  User,
  X,
  RefreshCw,
  Users
} from 'lucide-react';
import { useForm } from 'react-hook-form';
import { CreateTrainer, UpdateTrainer, GetAllTrainer, DeleteTrainer } from '../components/Api.jsx';

export default function TrainerManager() {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);

  const [trainers, setTrainers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmModal, setConfirmModal] = useState({ show: false, title: '', message: '', onConfirm: null });

  const { register, handleSubmit, reset, setValue, formState: { errors } } = useForm();
  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
  };

  // Predefined specialties
  const specialties = [
    'WEIGHT_LOSS',
    'MUSCLE_GAIN',
    'CARDIO',
    'YOGA',
    'PILATES',
    'CROSSFIT',
    'POWERLIFTING',
    'BODYBUILDING',
    'FUNCTIONAL_TRAINING',
    'SPORTS_SPECIFIC',
    'REHABILITATION',
    'NUTRITION',
    'PERSONAL_TRAINING',
    'GROUP_FITNESS'
  ];

  const fetchTrainers = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setIsLoading(true);
      }
      const response = await GetAllTrainer();
      console.log('Full API Response:', response);
      console.log('Response Data:', response.data);

      // Handle different possible response structures
      let trainersData = [];

      // Check if response.data is directly an array
      if (Array.isArray(response.data)) {
        trainersData = response.data;
      }
      // Check for nested structures
      else if (response.data && typeof response.data === 'object') {
        // Try different possible property names
        const possibleKeys = ['trainerCards', 'data', 'trainers', 'items', 'results'];

        for (const key of possibleKeys) {
          if (response.data[key] && Array.isArray(response.data[key])) {
            trainersData = response.data[key];
            console.log(`Found trainers in response.data.${key}`);
            break;
          }
        }

        // If still no data found, log the structure
        if (trainersData.length === 0) {
          console.log('Available keys in response.data:', Object.keys(response.data));
          console.log('Full response.data structure:', response.data);
        }
      }

      // Final fallback - if we still have no data, try to extract any array from the response
      if (trainersData.length === 0 && response.data) {
        console.log('Attempting to find any array in response...');
        const findArrays = (obj) => {
          if (Array.isArray(obj)) return obj;
          if (obj && typeof obj === 'object') {
            for (const value of Object.values(obj)) {
              const result = findArrays(value);
              if (result && result.length > 0) return result;
            }
          }
          return null;
        };

        const foundArray = findArrays(response.data);
        if (foundArray) {
          trainersData = foundArray;
          console.log('Found array in nested response:', foundArray);
        }
      }

      console.log('Processed trainers data:', trainersData);
      setTrainers(trainersData);
    } catch (error) {
      console.error('Error fetching trainers:', error);
      setTrainers([]);
    } finally {
      if (isRefresh) {
        setRefreshing(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  const handleRefresh = () => {
    fetchTrainers(true);
  };

  useEffect(() => {
    fetchTrainers();
  }, []);

  const onSubmit = async (data) => {
    // Check for duplicate email or mobile number
    const isEmailExists = trainers.some(t => t.email === data.email && t.id !== editingId);
    const isMobileExists = trainers.some(t => t.mobileNumber === data.mobileNumber && t.id !== editingId);

    if (isEmailExists) {
      showToast('A trainer with this email already exists.', 'error');
      return;
    }
    if (isMobileExists) {
      showToast('A trainer with this mobile number already exists.', 'error');
      return;
    }

    try {
      const file = data.trainerImage?.[0];
      if (editingId) {
        await UpdateTrainer(
          editingId,
          data.trainerName,
          data.email,
          data.primarySpecialty,
          data.yearsExperience,
          data.mobileNumber,
          data.bio,
          data.hourlyRate ? parseFloat(data.hourlyRate) : null, file);
      }
      else {
        await CreateTrainer(
          data.trainerName,
          data.email,
          data.primarySpecialty,
          parseInt(data.yearsExperience),
          data.mobileNumber,
          data.bio,
          data.hourlyRate ? parseFloat(data.hourlyRate) : null, file);
      } reset();
      setEditingId(null);
      setIsPopupOpen(false);
      fetchTrainers();
    } catch (error) {
      console.error('Error submitting trainer:', error); // Check if it's a duplicate key error 
      let errorMessage = 'Error saving trainer. Please try again.';
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        // Check for duplicate email
        if (errorData.includes('Key (email)')) {
          errorMessage = 'A trainer with this email already exists.';
        }
        // Check for duplicate mobile number
        else if (errorData.includes('Key (mobile_number)')) {
          errorMessage = 'A trainer with this mobile number already exists.';
        }
      }

      showToast(errorMessage, 'error');
    }
  };

  const handleEdit = (trainer) => {
    setEditingId(trainer.id);
    setValue('trainerName', trainer.trainerName || '');
    setValue('email', trainer.email || '');
    setValue('primarySpecialty', trainer.primarySpecialty || trainer.trainerSpecialty || '');
    setValue('yearsExperience', trainer.yearsExperience || trainer.trainerExperience || '');
    setValue('mobileNumber', trainer.mobileNumber || '');
    setValue('bio', trainer.bio || '');
    setValue('hourlyRate', trainer.hourlyRate || '');
    setIsPopupOpen(true);
  };

  const handleDelete = (trainer) => {
    setConfirmModal({
      show: true,
      title: 'Confirm Delete',
      message: `Are you sure you want to delete trainer "${trainer.trainerName}"? This action cannot be undone and will remove all their data from the system.`,
      onConfirm: async () => {
        try {
          await DeleteTrainer(trainer.id);
          showToast('Trainer deleted successfully');
          fetchTrainers();
        } catch (error) {
          console.error('Error deleting trainer:', error);
          showToast('Error deleting trainer. Please try again.', 'error');
        } finally {
          setConfirmModal(prev => ({ ...prev, show: false }));
        }
      }
    });
  };

  const openPopup = () => {
    setEditingId(null);
    reset();
    setIsPopupOpen(true);
  };


  return (
    <div className="min-h-screen p-4">
      {/* Toast Notification */}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
          <div className="flex items-center gap-2">
            {toast.type === 'error' ? <AlertCircle className="w-5 h-5" /> : <Check className="w-5 h-5" />}
            {toast.message}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-start gap-4">
                <div className="bg-orange-100 p-3 rounded-lg">
                  <Users className="w-6 h-6 text-orange-600" />
                </div>
                <div>
                  <p className="text-gray-600 text-sm leading-relaxed max-w-3xl">
                    This section contains trainers associated with the BYB. Add, remove, and edit trainer's information. Manage trainer profiles, specialties, and availability.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white rounded-lg transition-all duration-200 disabled:cursor-not-allowed text-sm font-medium shadow-sm hover:shadow focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                aria-label={refreshing ? "Refreshing data" : "Refresh trainers data"}
              >
                <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
              </button>
              <button
                onClick={openPopup}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg flex items-center gap-2 transition-colors text-sm"
              >
                <Plus className="w-5 h-5" />
                Add Trainer
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Popup Form */}
      {isPopupOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center p-6 border-b">
              <h2 className="text-2xl font-bold text-gray-900">
                {editingId ? 'Edit Trainer' : 'Add Trainer'}
              </h2>
              <button
                onClick={() => setIsPopupOpen(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
              {/* Compact row with 3 fields */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Trainer Name *
                  </label>
                  <input
                    {...register('trainerName', { required: 'Trainer name is required' })}
                    placeholder="Name"
                    className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.trainerName && (
                    <p className="text-red-600 text-xs mt-1">{errors.trainerName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <input
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Invalid email'
                      }
                    })}
                    type="email"
                    placeholder="Email"
                    className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.email && (
                    <p className="text-red-600 text-xs mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Mobile Number
                  </label>
                  <input
                    {...register('mobileNumber', {
                      required: 'Mobile number is required',
                      pattern: {
                        value: /^[0-9]{10}$/,
                        message: 'Invalid mobile number (10 digits required)'
                      }
                    })}
                    type="text"
                    placeholder="Mobile"
                    className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.mobileNumber && (
                    <p className="text-red-600 text-xs mt-1">{errors.mobileNumber.message}</p>
                  )}
                </div>
              </div>

              {/* Compact row with 3 fields */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Primary Specialty *
                  </label>
                  <select
                    {...register('primarySpecialty', { required: 'Specialty required' })}
                    className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="" disabled hidden>Select</option>
                    {specialties.map(specialty => (
                      <option key={specialty} value={specialty}>
                        {specialty.replace(/_/g, ' ')}
                      </option>
                    ))}
                  </select>
                  {errors.primarySpecialty && (
                    <p className="text-red-600 text-xs mt-1">{errors.primarySpecialty.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Years Experience *
                  </label>
                  <input
                    {...register('yearsExperience', {
                      required: 'Experience required',
                      min: { value: 0, message: 'Non-negative' },
                      max: { value: 50, message: 'Max 50 years' }
                    })}
                    type="number"
                    placeholder="Years"
                    className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.yearsExperience && (
                    <p className="text-red-600 text-xs mt-1">{errors.yearsExperience.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Hourly Rate (INR)
                  </label>
                  <input
                    {...register('hourlyRate', {
                      min: { value: 0, message: 'Non-negative' }
                    })}
                    type="number"
                    step="0.01"
                    placeholder="Rate"
                    className="w-full text-black px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.hourlyRate && (
                    <p className="text-red-600 text-xs mt-1">{errors.hourlyRate.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Bio
                </label>
                <textarea
                  {...register('bio')}
                  rows={2}
                  placeholder="Enter trainer bio..."
                  className="w-full text-black px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Profile Image {!editingId && '*'}
                </label>
                <input
                  {...register('trainerImage', { required: !editingId && 'Image required' })}
                  type="file"
                  accept="image/*"
                  className="w-full text-black px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                />
                {errors.trainerImage && (
                  <p className="text-red-600 text-sm mt-1">{errors.trainerImage.message}</p>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <button
                  type="button"
                  onClick={() => setIsPopupOpen(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-1 transition-colors text-sm"
                >
                  <Plus className="w-4 h-4" />
                  {editingId ? 'Update' : 'Add'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}


      {/* Trainer List */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          <p className="text-gray-600 mt-2 text-sm">Loading trainers...</p>
        </div>
      ) : trainers.length === 0 ? (
        <div className="text-center py-12">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No trainers found</p>
          <p className="text-gray-500 text-sm mt-1">Get started by adding your first trainer</p>
          <button
            onClick={fetchTrainers}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            Refresh Data
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {trainers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((trainer, index) => (
            <motion.div
              key={trainer.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 p-2.5 flex flex-col"
            >
              {/* Top Row */}
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {/* Avatar */}
                  {trainer.profileImageUrl || trainer.imageUrl ? (
                    <img
                      src={trainer.profileImageUrl || trainer.imageUrl}
                      alt={trainer.trainerName}
                      className="w-24 h-24 rounded-full object-cover border"
                    />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center border">
                      <User className="w-24 h-24 text-gray-900" />
                    </div>
                  )}

                  {/* Name & Specialty */}
                  <div className="min-w-0">
                    <h3 className="text-xl font-semibold text-gray-900 truncate leading-tight">
                      {trainer.trainerName}
                    </h3>
                    <p className="text-[14px] text-indigo-600 truncate">
                      {(trainer.primarySpecialty || trainer.trainerSpecialty || 'Not Set')
                        .replace(/_/g, ' ')}
                    </p>
                  </div>
                </div>

                {/* Status */}
                <span
                  className={`px-2 py-0.5 rounded-full text-[16px] font-semibold ${trainer.isActive !== false
                    ? 'bg-green-100 text-green-800'
                    : 'bg-red-100 text-red-800'
                    }`}
                >
                  {trainer.isActive !== false ? 'Active' : 'Inactive'}
                </span>
              </div>

              {/* Stats Row – tightly packed */}
              <div className="mt-2.5 flex items-center justify-between px-1">
                <div className="flex items-center gap-1 text-md font-medium">
                  <Clock className="w-3 h-3 text-indigo-500" />
                  <span className="text-[14px] text-gray-500">{trainer.yearsExperience || trainer.trainerExperience || 0}</span>
                  <span className="text-[14px] text-gray-500">yrs</span>
                </div>

                <div className="flex items-center gap-1 text-md font-medium">
                  <Star className="w-4 h-4 text-yellow-400 fill-current" />
                  <span className="text-[14px] text-gray-500">{trainer.averageRating?.toFixed(1) || '0.0'}</span>
                </div>

                <div className="flex items-center gap-0.5 text-md font-medium">
                  <span className="text-[14px] text-gray-500">₹{trainer.hourlyRate ?? 'N/A'}</span>
                  <span className="text-[14px] text-gray-500">/hr</span>
                </div>
              </div>

              {/* Contact + Actions */}
              <div className="mt-2.5 flex items-center justify-between gap-2">
                <div className="space-y-0.5 min-w-0 text-[11px] text-gray-600">
                  {trainer.email && (
                    <div className="flex items-center gap-1 truncate">
                      <Mail className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="truncate text-[14px] text-gray-500">{trainer.email}</span>
                    </div>
                  )}
                  {trainer.mobileNumber && (
                    <div className="flex items-center gap-1">
                      <Phone className="w-4 h-4 text-gray-400 shrink-0" />
                      <span className="text-[14px] text-gray-500">{trainer.mobileNumber}</span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-1 shrink-0">
                  <button
                    onClick={() => handleEdit(trainer)}
                    className="p-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md"
                    title="Edit"
                  >
                    <Edit className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => handleDelete(trainer)}
                    className="p-1.5 bg-red-600 hover:bg-red-700 text-white rounded-md"
                    title="Delete"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </motion.div>

          ))}
        </div>
      )}
      {/* Custom Confirm Modal */}
      {confirmModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 backdrop-blur-sm transition-all duration-300">
          <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl transform transition-all duration-300 scale-100 p-6 border border-gray-100">
            <div className="flex flex-col items-center text-center">
              <div className="bg-red-50 p-4 rounded-full mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                {confirmModal.title}
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-8">
                {confirmModal.message}
              </p>
              <div className="flex gap-3 w-full">
                <button
                  onClick={() => setConfirmModal(prev => ({ ...prev, show: false }))}
                  className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmModal.onConfirm}
                  className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white rounded-xl shadow-lg shadow-red-200 transition-all duration-200 font-semibold text-sm focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-offset-2"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
          {trainers?.length > 0 && (

      <Pagination 
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          totalPages={Math.ceil((Array.isArray(trainers) ? trainers.length : (trainers?.length || 0)) / itemsPerPage)}
          totalElements={(Array.isArray(trainers) ? trainers.length : (trainers?.length || 0))}
          pageSize={itemsPerPage}
          setPageSize={setItemsPerPage}
        />
      )}
</div>
  );
}